import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Table,
  Button,
  Input,
  Badge,
  Pagination,
  PaginationItem,
  PaginationLink,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  InputGroup,
  Spinner,
  CardTitle,
  CardText,
  CardImg,
} from "reactstrap";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import blog from "../../../assets/kangaroo/blog.jpg";
import blog1 from "../../../assets/kangaroo/Australian.png";
import blog2 from "../../../assets/kangaroo/Canada Course.png";
import BlogService from "../../../services/KangarooServices/BlogService";
import { Link, useNavigate } from "react-router-dom";
import DeleteModal from "../../../Components/Common/DeleteModal";
import toast from "react-hot-toast";
import { imageBaseApi } from "../../../utilis/imageHelper";

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [meta, setMeta] = useState({
    totalCount: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    status: "",
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  // Delete modal states
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);

  const toggleDelete = () => setIsOpenDelete(!isOpenDelete);
  const navigate = useNavigate();

  // -----------------------------
  // Fetch Blog (server-side)
  // -----------------------------
  const fetchBlog = async (
    page = currentPage,
    size = pageSize,
    currentFilters = debouncedFilters
  ) => {
    setLoading(true);
    try {
      const response = await BlogService.getList({
        ...currentFilters,
        page,
        pageSize: size,
      });

      setBlogs(response.items || []);

      // Use the correct fields from API response
      setMeta({
        totalCount: response.totalCount || 0,
        page: response.page || page,
        pageSize: response.pageSize || size,
        totalPages: response.totalPages || 1, // This is key!
      });
    } catch (error) {
      console.log(error);
      toast.error("Failed to load blogs");
    } finally {
      setLoading(false);
    }
  };
  const totalPages = meta.totalPages || 1;
  // initial load
  useEffect(() => {
    fetchBlog();
  }, []);

  // debounce filters
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedFilters(filters);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(t);
  }, [filters]);

  // refetch on debounced filter update
  useEffect(() => {
    fetchBlog(1);
  }, [debouncedFilters]);

  // handle search + status change
  const handleSearchChange = (e) =>
    setFilters((prev) => ({ ...prev, search: e.target.value }));

  const handleStatusChange = (e) =>
    setFilters((prev) => ({ ...prev, status: e.target.value }));

  // delete
  const handleDeleteClick = (item) => {
    setSelectedDeleteItem(item);
    toggleDelete();
  };

  const handleDelete = async () => {
    if (selectedDeleteItem) {
      try {
        await BlogService.delete(selectedDeleteItem);
        fetchBlog();
        toast.success("Blog deleted successfully");
      } catch (error) {
        toast.error("Delete failed");
      } finally {
        toggleDelete();
        setSelectedDeleteItem(null);
      }
    }
  };

  // normalize blog status
  const normalizeStatus = (status) => {
    const s = String(status).toLowerCase();
    if (s === "0" || s === "draft") return "draft";
    if (s === "1" || s === "published") return "published";
    if (s === "2" || s === "archived") return "archived";
    return "draft";
  };

  const statusMap = {
    draft: { label: "Draft", color: "warning" },
    published: { label: "Published", color: "success" },
    archived: { label: "Archived", color: "secondary" },
  };

  // pagination click
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchBlog(page);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Blog List" pageTitle="Blog" />

        {/* Filters */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center ">
              <button
                className="btn btn-success"
                onClick={() => navigate("/blog/create")}
              >
                <i className="bi bi-plus-lg"></i> New Blog
              </button>
            </div>
          </Col>
          <Col
            className="d-flex justify-content-end align-items-end mb-2"
            md={6}
          >
            <InputGroup className="w-auto">
              <Input
                placeholder="Search..."
                value={filters.search}
                onChange={handleSearchChange}
              />
            </InputGroup>
          </Col>

          <Col md={4}>
            <Input
              type="select"
              value={filters.status}
              onChange={handleStatusChange}
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </Input>
          </Col>
        </Row>

        {/* Blog List */}
        <Row xs="1" md="2" lg="3" className="g-4">
          {blogs.map((blog) => {
            const statusKey = normalizeStatus(blog.status);
            const { label, color } = statusMap[statusKey];

            return (
              <Col key={blog.id}>
                <Card className="h-100 shadow-sm border-0 rounded-4">
                  <div
                    className="position-relative"
                    style={{ aspectRatio: "16/9", overflow: "hidden" }}
                  >
                    <CardImg
                      src={`${imageBaseApi}${blog?.coverMedia?.url}`}
                      className="card-img-top w-100 h-100"
                      alt={blog.title}
                      loading="lazy"
                      style={{ objectFit: "contain" }}
                    />
                    <Badge
                      color={color}
                      className="position-absolute top-0 end-0 m-2 px-3 py-2 rounded-pill shadow-sm"
                    >
                      {label}
                    </Badge>
                    {blog?.isFeatured === true ? (
                      <Badge
                        color={color}
                        className="position-absolute top-0 start-0 m-2 px-3 py-2 rounded-pill shadow-sm"
                      >
                        {blog?.isFeatured === true ? "Featured" : ""}
                      </Badge>
                    ) : null}
                  </div>

                  <CardBody className="d-flex flex-column">
                    <CardTitle
                      tag="h5"
                      className="fw-bold text-dark mb-2 two-line-title"
                    >
                      {blog.title}
                    </CardTitle>

                    <CardText className="text-muted small  ">
                      {blog.shortDescription?.slice(0, 120) ||
                        "No description."}
                      {blog.shortDescription?.length > 120 && "..."}
                    </CardText>

                    <div className="d-flex justify-content-between text-muted small mb-2">
                      <span>⏱ {blog.readingTime || 1} min read</span>

                      <span>
                        {blog.publishedAt
                          ? new Date(blog.publishedAt).toLocaleDateString()
                          : "Not Published"}
                      </span>
                    </div>

                    <small className="text-muted mb-2">
                      by {blog.author || "Admin"}
                    </small>

                    <div className="d-flex justify-content-between align-items-center mt-auto">
                      <Link
                        to={`/blog/${blog.slug}`}
                        className="card-link link-secondary text-decoration-none fw-semibold d-flex align-items-center"
                      >
                        View Details
                        <i className="ri-arrow-right-s-line ms-1 align-middle lh-1"></i>
                      </Link>
                      <div>
                        <Button
                          size="sm"
                          color="primary"
                          className="me-2 rounded-pill"
                          onClick={() => navigate(`/blog/update/${blog.slug}`)}
                        >
                          <i className="bx bx-edit-alt"></i>
                        </Button>

                        <Button
                          size="sm"
                          color="danger"
                          className="rounded-pill"
                          onClick={() => handleDeleteClick(blog.id)}
                        >
                          <i className="ri-delete-bin-5-line"></i>
                        </Button>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            );
          })}
        </Row>

        {/* No results */}
        {blogs.length === 0 && (
          <Card className="text-center p-5 my-5">
            <CardBody>
              <h4>No blogs found</h4>
              <p className="text-muted">Try adjusting filters or search.</p>
            </CardBody>
          </Card>
        )}

        {/* Pagination */}
        {/* Pagination */}
        {/* Improved Pagination - Shows only 5 pages at a time */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-4 sticky-bottom bottom-0">
            <ul className="pagination pagination-md">
              {/* Previous Button */}
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
              </li>

              {/* First Page */}
              {currentPage > 3 && (
                <>
                  <li className="page-item">
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(1)}
                    >
                      1
                    </button>
                  </li>
                  {currentPage > 4 && (
                    <li className="page-item disabled">
                      <span className="page-link">...</span>
                    </li>
                  )}
                </>
              )}

              {/* Visible Page Numbers (max 5) */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let page;
                if (totalPages <= 5) {
                  page = i + 1;
                } else {
                  // Calculate start of visible window
                  const start = Math.max(1, currentPage - 2);
                  const end = Math.min(totalPages, start + 4);
                  page = start + i;
                  if (page > end) return null;
                }

                return (
                  <li
                    key={page}
                    className={`page-item ${
                      page === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  </li>
                );
              })}

              {/* Last Page with Ellipsis */}
              {currentPage < totalPages - 2 && (
                <>
                  {currentPage < totalPages - 3 && (
                    <li className="page-item disabled">
                      <span className="page-link">...</span>
                    </li>
                  )}
                  <li className="page-item">
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(totalPages)}
                    >
                      {totalPages}
                    </button>
                  </li>
                </>
              )}

              {/* Next Button */}
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </li>
            </ul>
          </div>
        )}
      </Container>

      <DeleteModal
        show={isOpenDelete}
        onCloseClick={toggleDelete}
        onDeleteClick={handleDelete}
      />
    </div>
  );
};

export default Blog;
